<template>
  <div style="width: 100%">
    <div class="readiness-container">
      <PageTitle class="page-title" title="Directories" />
      <EmployeeReadinessMenu :items="menuItems" @onChange="onChangeMenu" />
    </div>
    <v-tabs-items v-model="selectedTab" class="custom-tabs">
      <v-tab-item v-for="(item, index) in menuItems" :key="index">
        <v-card elevation="0" class="employee-readiness-container">
          <template v-if="item.title === 'Employee Directory'">
            <div v-if="employeeDirectoryViewState.state.showEmployeeBadgesList">
              <EmployeeBadgesList :dataSource="badgesDataSource" />
            </div>
            <div v-else>
              <EmployeeDirectoryView :dataSource="dataSource" />
            </div>
          </template>

          <template v-else-if="item.title === 'Responsibilities Directory'">
            <div
              v-if="
                responsibilityDirectoryViewState.state.showCurrentBageHolderList
              "
            >
              <ResponsibilityCurrentBadgeHolderList
                :selectedResponsibility="
                  responsibilityDirectoryViewState.state.selectedResponsibility
                "
                :selectedBadgeOption="
                  responsibilityDirectoryViewState.state.selectedBadgeOption
                "
                @onClickBreadcrumb="onClickBreadcrumb"
              />
            </div>
            <div v-else>
              <ResponsibilityDirectoryView :respDataSource="respDataSource" />
            </div>
          </template>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import PageTitle from "@components/common/PageTitle.vue";
import EmployeeReadinessMenu from "@components/EmployeeReadinessMenu/EmployeeReadinessMenu.vue";
import EmployeeDirectoryView from "./EmployeeDirectory/EmployeeDirectoryView.vue";
import EmployeeBadgesList from "./EmployeeBadgesList/EmployeeBadgesListView.vue";
import { useEmployeeDirectoryViewState } from "./EmployeeDirectory/composables/useViewState";
import { useViewState } from "./ResponsibilityDirectory/composables/useViewState";
import ResponsibilityDirectoryView from "./ResponsibilityDirectory/ResponsibilityDirectoryView.vue";
import {
  getDirectoryEmployees,
  getEmployeeBadgesList,
  getDirectoryResponsibilities,
  getAccountData,
} from "@/api";
import ResponsibilityCurrentBadgeHolderList from "./ResponsibilityCurrentBadgeHolderList.vue";
export default {
  components: {
    PageTitle,
    EmployeeReadinessMenu,
    EmployeeDirectoryView,
    EmployeeBadgesList,
    ResponsibilityDirectoryView,
    ResponsibilityCurrentBadgeHolderList,
  },
  data() {
    return {
      dataSource: [],
      respDataSource: [],
      badgesDataSource: [],
      accountID: null,
      authUser: null,
      topLevel: "position",
      removeBorder: false,
      selectedTab: 0,
      employeeDirectoryViewState: useEmployeeDirectoryViewState(),
      responsibilityDirectoryViewState: useViewState(),
      currentAccountDetails: {},
    };
  },
  async mounted() {
    this.accountID = JSON.parse(localStorage.getItem("currentAccountID"));
    await this.getAccountDetails();
    // Load initial data based on default selected tab (index 0)
    // After account details are loaded, menuItems will be populated
    if (this.menuItems.length > 0) {
      const firstTabTitle = this.menuItems[0]?.title;
      if (firstTabTitle === "Employee Directory") {
        await this.getDirectoryEmployees();
      } else if (firstTabTitle === "Responsibilities Directory") {
        await this.getDirectoryResponsibilities();
      }
    }
  },
  computed: {
    menuItems() {
      const items = [];
      if (this.currentAccountDetails?.show_employee_directory) {
        items.push({
          title: "Employee Directory",
          count: this.dataSource?.filter((el) => el.current_status === "active")
            .length,
        });
      }
      if (this.currentAccountDetails?.show_responsibilities_directories) {
        items.push({
          title: "Responsibilities Directory",
          count: this.respDataSource?.length,
        });
      }
      return items;
    },
  },
  watch: {
    "employeeDirectoryViewState.state.showEmployeeBadgesList"(newVal) {
      if (newVal) {
        this.onShowBadgesList();
      } else {
        this.getDirectoryEmployees();
      }
    },
    "responsibilityDirectoryViewState.state.showCurrentBageHolderList"(newVal) {
      if (!newVal) {
        this.getDirectoryResponsibilities();
      }
    },
  },

  methods: {
    onChangeMenu(index) {
      this.selectedTab = index;
      this.responsibilityDirectoryViewState.state.showCurrentBageHolderList = false;
      this.responsibilityDirectoryViewState.state.selectedResponsibility = null;
      this.responsibilityDirectoryViewState.state.selectedBadgeOption = null;

      // Call appropriate API based on selected tab
      const selectedTabTitle = this.menuItems[index]?.title;

      if (selectedTabTitle === "Employee Directory") {
        this.getDirectoryEmployees();
      } else if (selectedTabTitle === "Responsibilities Directory") {
        this.getDirectoryResponsibilities();
      }
      // For 'Directories' tab, no additional API call needed as it uses currentAccountDetails
    },
    async getAccountDetails() {
      try {
        this.$loader.show();
        const response = await getAccountData(this.accountID);
        this.currentAccountDetails = response?.data;
      } catch (e) {
        console.log("e: ", e);
      } finally {
        this.$loader.hide();
      }
    },
    async getDirectoryEmployees() {
      try {
        this.$loader.show();
        const { data } = await getDirectoryEmployees(this.accountID);
        if (data?.length) {
          this.dataSource = await this.transformData(data);
        } else {
          this.dataSource = [];
        }
      } catch (error) {
        console.error("Failed to fetch directory employees:", error);
      } finally {
        this.$loader.hide();
      }
    },
    async getDirectoryResponsibilities() {
      try {
        this.$loader.show();
        const { data } = await getDirectoryResponsibilities(this.accountID);
        if (data?.length) {
          this.respDataSource = await this.transformRespData(data);
        } else {
          this.respDataSource = [];
        }
      } catch (error) {
        console.error("Failed to fetch directory responsibilities:", error);
      } finally {
        this.$loader.hide();
      }
    },
    transformData(dataArray) {
      return dataArray.map((data) => ({
        id: data.id,
        first_name: `${data.first_name}`,
        last_name: `${data.last_name}`,
        position:
          data?.primary_positions.length > 0
            ? data.primary_positions[0].name
            : "",
        checkpoint: data.badge_counts,
        avatar: data.avatar,
        current_readiness: data.current_readiness,
        current_status: data.current_status,
      }));
    },
    transformRespData(dataArray) {
      return dataArray.map((data) => {
        const masterFullNames = data.masters.map((item) => item.full_name);

        const coach_badges = data.coach
          .filter((item) => !masterFullNames.includes(item.full_name))
          .map((item) => {
            const [firstName, lastName] = item.full_name.split(" ");
            return {
              img: item.avatar,
              fullName: item.full_name,
              firstName,
              lastName,
              badge_level: "coach",
            };
          });

        const master_badges = data.masters.map((item) => {
          const [firstName, lastName] = item.full_name.split(" ");
          return {
            img: item.avatar,
            fullName: item.full_name,
            firstName,
            lastName,
            badge_level: "master",
          };
        });

        const apprentice_badges = data.apprentice.map((item) => {
          const [firstName, lastName] = item.full_name.split(" ");
          return {
            img: item.avatar,
            full_name: item.full_name,
            firstName,
            lastName,
            badge_level: "apprentice",
          };
        });

        const professional_badges = data.professional.map((item) => {
          const [firstName, lastName] = item.full_name.split(" ");
          return {
            img: item.avatar,
            full_name: item.full_name,
            firstName,
            lastName,
            badge_level: "professional",
          };
        });

        return {
          id: data.id,
          name: data.name,
          code: this.extractCode(data.code_str),
          coach_badges,
          master_badges,
          apprentice_badges,
          professional_badges,
          checkpoint: {
            apprentice: data.badge_apprentice_holder_count,
            professional: data.badge_professional_holder_count,
            master: data.badge_master_holder_count,
            coach: data.badge_coach_holder_count,
          },
          all_badges: [
            ...coach_badges,
            ...master_badges,
            ...apprentice_badges,
            ...professional_badges,
          ],
        };
      });
    },

    async onShowBadgesList() {
      try {
        this.$loader.show();
        const emp_id =
          this.employeeDirectoryViewState.state.selectedEmployee.id;
        const { data } = await getEmployeeBadgesList(this.accountID, emp_id);
        if (data?.length) {
          this.badgesDataSource = await this.transformBadgesList(data);
        } else {
          this.badgesDataSource = [];
        }
      } catch (error) {
        console.error("Failed to fetch employee badges list:", error);
      } finally {
        this.$loader.hide();
      }
    },
    transformBadgesList(data) {
      return data
        .filter(({ is_current }) => is_current)
        .map(({ id, issued_for = {}, level, issued_by = {}, issued_on }) => ({
          id,
          name: issued_for.name,
          code: this.extractCode(issued_for.code),
          badge: {
            level,
            view: "LargePlain",
          },
          issued_by: {
            name: issued_by.name,
            picture: issued_by.picture,
            userType: "user",
            date: issued_on,
          },
        }));
    },
    extractCode(str) {
      let match = str?.match(/#\d+/);
      return match ? match[0] : null;
    },
    onClickBreadcrumb(breadcrumb) {
      if (breadcrumb) {
        this.responsibilityDirectoryViewState.state.showCurrentBageHolderList = false;
        this.responsibilityDirectoryViewState.state.selectedResponsibility =
          null;
        this.responsibilityDirectoryViewState.state.selectedBadgeOption = null;
      }
    },
  },
};
</script>

<style lang="css" scoped>
.readiness-container {
  padding-left: 15px;
  width: 100%;
}

.page-title {
  margin-top: 16px;
}

.employee-path-to-readiness {
  /* box-shadow: 0px 1px 2px 0px #1018280d; */
  border-radius: 8px;
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding: 20px;
}
.employee-readiness-title {
  font-family: Inter;
  font-size: 18px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  color: #344054;
  min-width: 125px;
}
.employee-readiness {
  width: 100%;
  margin-left: 15px;
}
.employee-readiness .progress-bar-container {
  width: 100%;
}
.employee-readiness .progress-value {
  font-weight: 600;
}
.employee-path {
  border-radius: 8px;
}
::v-deep .progress-bar-container {
  width: 100%;
}
.employee-readiness-container {
  width: 100%;
  margin-left: 4px;
}

#treelist {
  max-width: 100%;
}

::v-deep(.v-tabs) {
  padding: 13px 0px !important;
  border-bottom: 1px solid #eaecf0 !important;
}

::v-deep(.custom-tabs) {
  top: 20px;
}
.directories-container {
  width: 98%;
  border: 1px solid #e5e5e5;
  border-radius: 8px !important;
  padding: 12px;
  margin: 12px 24px 12px 12px;
}
</style>
